import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useContentDStore } from '@/stores/useContentDStore'
import { formatDistanceToNow } from 'date-fns'
import { FileText, Loader2 } from 'lucide-react'
import type { ContentFoolscapSummary } from '@/lib/api'

// =============================================================================
// Component
// =============================================================================

/**
 * ContentDFoolscapPicker - Modal for selecting a completed Content Foolscap.
 *
 * Single-step flow:
 * 1. Select a Content F session from the list (completed Foolscaps from content_f workflow)
 * 2. Create Content D session - Gatekeeper will gather document preferences conversationally
 */
export function ContentDFoolscapPicker() {
  const {
    showFoolscapPicker,
    closeFoolscapPicker,
    availableFoolscaps,
    isLoadingFoolscaps,
    fetchAvailableFoolscaps,
    createSessionFromFoolscap,
  } = useContentDStore()

  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (showFoolscapPicker) {
      fetchAvailableFoolscaps()
    }
  }, [showFoolscapPicker, fetchAvailableFoolscaps])

  if (!showFoolscapPicker) return null

  const handleSelectFoolscap = async (foolscap: ContentFoolscapSummary) => {
    setIsCreating(true)
    try {
      await createSessionFromFoolscap(
        foolscap.session_id,
        foolscap.name || foolscap.entity_name || undefined
      )
    } finally {
      setIsCreating(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/10 backdrop-blur-sm"
        onClick={closeFoolscapPicker}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 max-h-[80vh] bg-background border border-border rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-border">
          <h2 className="text-lg font-light text-foreground">
            Select a Content Foolscap
          </h2>
          <p className="mt-1 text-sm font-light text-muted-foreground">
            Choose a completed Content Foolscap to draft.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4">
          {isLoadingFoolscaps ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : availableFoolscaps.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" strokeWidth={1.5} />
              <h3 className="text-sm font-light text-foreground mb-1">
                No completed Content Foolscaps
              </h3>
              <p className="text-sm font-light text-muted-foreground">
                Complete a Content Foolscap in /content_f first.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableFoolscaps.map((foolscap) => (
                <button
                  key={foolscap.session_id}
                  onClick={() => handleSelectFoolscap(foolscap)}
                  disabled={isCreating}
                  className="w-full text-left p-4 rounded-md border border-border hover:border-foreground/30 hover:bg-muted/50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-light text-foreground truncate group-hover:text-foreground">
                        {foolscap.name || foolscap.entity_name || 'Untitled Foolscap'}
                      </h3>
                      {foolscap.foolscap_markdown && (
                        <p className="mt-1 text-xs font-light text-muted-foreground line-clamp-2">
                          {foolscap.foolscap_markdown.slice(0, 120)}...
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-[10px] font-light uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(foolscap.updated_at), { addSuffix: true })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-border">
          <div className="flex justify-between items-center">
            <p className="text-xs font-light text-muted-foreground">
              Gatekeeper will ask about format preferences
            </p>
            <button
              onClick={closeFoolscapPicker}
              className="px-4 py-2.5 text-sm font-light text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
